<template>
  <div class="ratings-content section-padding">
    <div class="container">
      <div v-if="ratings && ratings.length > 0" ref="ratingsSwiperRoot">
          <div class="ratings-summary">
            <p class="ratings-summary__score">{{ getGlobalRatingValue() }}</p>
            <div class="ratings-summary__meta">
              <Rating max="5" size="small" :value="getGlobalRatingValue()" />
              <p class="ratings-summary__count">{{ reviewCountLabel(ratings.length) }}</p>
            </div>
          </div>

          <div class="ratings-carousel">
            <div class="swiper-container">
              <div class="swiper-wrapper">
                <div
                  class="swiper-slide"
                  :key="rating._id"
                  v-for="rating in ratings"
                >
                  <article class="rating-card">
                    <header class="rating-card__header">
                      <Rating max="5" size="small" :value="rating.rating" />
                      <time class="rating-card__date">{{ getYear(rating.date || rating.createdAt) }}</time>
                    </header>
                    <p class="rating-card__author">{{ rating.guest_name || 'Anonym' }}</p>
                    <div
                      class="rating-card__text"
                      v-html="formatReviewText(rating.review_text)"
                    ></div>
                  </article>
                </div>
              </div>
            </div>
            <div v-if="ratings.length > 1" class="text-center ratings-carousel__pagination">
              <div class="swiper-pagination"></div>
            </div>
          </div>

          <div class="ratings-actions">
          <button type="button" class="button light" @click="new_rating_is_open = true">
            Neue Bewertung
          </button>
        </div>
      </div>

      <div v-else class="ratings-empty">
        <p>Noch keine Bewertungen für dieses Apartment vorhanden.</p>
        <button type="button" class="button light" @click="new_rating_is_open = true">
          Erste Bewertung schreiben
        </button>
      </div>
    </div>
  </div>

  <PopUp
    :is_open="new_rating_is_open"
    @onClose="new_rating_is_open = false"
    auto_height
    size="half"
    title="Neue Bewertung"
  >
    <template #content>
      <NewRatingForm v-bind:appartment_id="appartment_id" />
    </template>
  </PopUp>
</template>

<script>
import moment from "moment";
import Rating from "../misc/Rating.vue";
import PopUp from "../misc/PopUp.vue";
import NewRatingForm from "../misc/NewRatingForm.vue";
import {
  SWIPER_MOBILE_BREAKPOINTS,
  cancelScheduledSwiperInit,
  destroySwiperInRoot,
  scheduleSwiperInit,
} from "../../lib/swiperInit.js";

/** Swiper 3.x: autoHeight passt die Höhe nativ beim Init und Slide-Wechsel an */
function getRatingsSwiperOptions() {
  return {
    slidesPerView: 1,
    spaceBetween: 20,
    autoHeight: true,
    speed: 320,
    freeMode: false,
    longSwipes: true,
    longSwipesRatio: 0.28,
    longSwipesMs: 260,
    shortSwipes: true,
    threshold: 8,
    touchAngle: 35,
    breakpoints: SWIPER_MOBILE_BREAKPOINTS,
  };
}

export default {
  methods: {
    openNewRating() {
      this.new_rating_is_open = true
    },
    getYear(date) {
      return moment(date).format("DD.MM.YYYY");
    },
    reviewCountLabel(count) {
      return count === 1 ? "1 Bewertung" : `${count} Bewertungen`;
    },
    formatReviewText(text) {
      if (!text) return "Keine Bewertung";
      return String(text)
        .replace(/\\n/g, "<br>")
        .replace(/\n/g, "<br>");
    },
    getGlobalRatingValue() {
      let value = 0;
      if (this.ratings && this.ratings.length > 0) {
        this.ratings.forEach((rating) => {
          value = value + (rating.rating || 0);
        });
        return Math.round((value / this.ratings.length) * 10) / 10;
      }

      return 0;
    },
    initRatingsSwiper() {
      if (!this.ratings?.length || !this.$refs.ratingsSwiperRoot) return;

      scheduleSwiperInit(this.$refs.ratingsSwiperRoot, getRatingsSwiperOptions());
    },
  },
  data() {
    return {
      new_rating_is_open: false,
    };
  },
  components: {
    Rating,
    PopUp,
    NewRatingForm,
  },
  props: {
    ratings: Array,
    appartment_id: [Number, String],
  },
  watch: {
    ratings: {
      handler() {
        setTimeout(() => {
          this.initRatingsSwiper();
        }, 100);
      },
      immediate: true,
      deep: true,
    },
  },
  mounted() {
    setTimeout(() => {
      this.initRatingsSwiper();
    }, 100);
  },
  beforeUnmount() {
    cancelScheduledSwiperInit(this.$refs.ratingsSwiperRoot);
    destroySwiperInRoot(this.$refs.ratingsSwiperRoot);
  },
};
</script>

<style scoped lang="scss">
.section-padding {
  padding-bottom: 60px;
}

.ratings-carousel :deep(.swiper-container) {
  overflow: hidden;
  touch-action: pan-y;
}

.ratings-carousel :deep(.swiper-container-autoheight) {
  transition: height 0.32s ease;
}

.ratings-carousel :deep(.swiper-wrapper) {
  align-items: flex-start;
  transition-timing-function: ease-out;
}

.ratings-carousel :deep(.swiper-slide) {
  height: auto;
  box-sizing: border-box;
  padding-bottom: 12px;
}

.ratings-carousel__pagination {
  margin-top: 0.5rem;
}

.ratings-summary {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  margin-bottom: 1.75rem;
  padding: 1.25rem 1.5rem;
  border-radius: 16px;
  background: rgba(66, 217, 228, 0.08);
  border: 1px solid rgba(66, 217, 228, 0.18);
}

.ratings-summary__score {
  margin: 0;
  font-size: clamp(2.5rem, 6vw, 3.25rem);
  font-weight: 400;
  line-height: 1;
  color: #2f363a;
}

.ratings-summary__meta {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.35rem;
}

.ratings-summary__count {
  margin: 0;
  font-size: 0.875rem;
  color: #6b7280;
}

.ratings-summary :deep(.fa-star) {
  font-size: 1rem;
}

.rating-card {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  padding: 1.35rem 1.4rem 1.5rem;
  border-radius: 16px;
  background: #fff;
  border: 1px solid rgba(81, 90, 95, 0.12);
  box-shadow: 0 4px 18px rgba(15, 23, 42, 0.07);
}

.rating-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.65rem;
}

.rating-card__date {
  font-size: 0.8125rem;
  color: #6b7280;
  white-space: nowrap;
}

.rating-card__author {
  margin: 0 0 0.85rem;
  font-size: 0.9375rem;
  font-weight: 700;
  color: #2f363a;
  line-height: 1.35;
}

.rating-card__text {
  font-size: 0.9375rem;
  line-height: 1.65;
  color: #515a5f;
  overflow-wrap: anywhere;
  word-break: break-word;

  :deep(p) {
    margin: 0;
    text-align: left;
  }
}

.rating-card :deep(.fa-star.small) {
  font-size: 0.95rem;
}

.ratings-actions {
  margin-top: 0.5rem;
  text-align: center;
}

.ratings-empty {
  padding: 2.5rem 1rem;
  text-align: center;
  color: #666;

  p {
    margin: 0 0 1.25rem;
  }
}

@media (max-width: 767px) {
  .section-padding {
    padding-bottom: 1.5rem;
  }

  .ratings-summary {
    flex-direction: column;
    gap: 0.65rem;
    margin-bottom: 1.25rem;
    padding: 1rem 1.15rem;
  }

  .ratings-summary__meta {
    align-items: center;
  }

  .rating-card {
    padding: 1.15rem 1.1rem 1.25rem;
  }

  .rating-card__header {
    flex-direction: column;
    align-items: flex-start;
  }

  .rating-card__date {
    white-space: normal;
  }
}
</style>
